// Rückblick am Ende einer Legislatur: Was hat die Regierung geschafft, bevor der nächste Wahlkampf beginnt?
import { S, log } from './state';
import { PA } from './rules';
import { poll } from './govern';
import { dateYear } from './util';
import { evalPromises, startCampaign } from './campaign';
import { isVersus, ROLES, pl, isChan, actor, type Role } from './versus';
import { AX } from '../content/axes';
import { WENDE_MAX } from './zeitgeist';
import { clamp } from './util';
import { mpCampStart } from './mpcamp';
import { axes } from './axes';

type Stats = { bills: number; billsOk: number; suits: number; suitsWon: number; attacks: number; misstrauen: number };

/** Stand zu Beginn der Legislatur, damit der Rückblick Veränderungen zeigen kann. */
export interface LegStart { m: number; debt: number; share: number; crises: number; stats?: Partial<Record<Role, Stats>> }

export interface Review {
  leg: number; from: number; to: number; early: boolean;
  gov: string[]; chan: string; minority: boolean;
  laws: { t: string; by?: string }[];
  repealed: string[];
  voided: string[];
  budgets: { y: number; saldo: number; ok: boolean }[];
  debt0: number; debt1: number; saldo: number;
  share0: number; poll1: number;
  promises: { t: string; state: string }[];
  scandals: number; crises: number;
  players?: { role: Role; name: string; role2: string; d: Stats }[];
  /** Programm beim Öffnen des Rückblicks, je Partei (Einzelspieler: 'YOU'). */
  pos0?: Record<string, Record<string, number>>;
}

const zero: Stats = { bills: 0, billsOk: 0, suits: 0, suitsWon: 0, attacks: 0, misstrauen: 0 };

/** Beim Amtsantritt aufrufen. */
export function legStartSnap() {
  const st: LegStart = { m: S.m, debt: S.debt, share: S.elec || 0, crises: (S.crisisHist || []).length };
  if (isVersus()) { st.stats = {}; for (const r of ROLES) st.stats[r] = { ...pl(r).stats }; }
  S.legStart = st;
}

function build(early: boolean): Review {
  const ls: LegStart = S.legStart || { m: S.m0, debt: S.debt, share: S.elec || 0, crises: 0 };
  const inLeg = (m: unknown) => typeof m === 'number' && m >= S.m0;
  const done = S.laws.filter((L) => ['kraft', 'done'].includes(L.stage) && (L.leg ?? 0) === S.leg && !L.bestand && !L.rep);
  const r: Review = {
    leg: S.leg, from: S.legYear, to: dateYear(S.m), early,
    gov: ['YOU', ...S.partners].map((p) => PA(p)), chan: S.name, minority: !!S.minority,
    laws: done.filter((L, i) => done.findIndex((o) => o.t === L.t) === i).map((L) => ({ t: L.t, by: L.by && L.by !== 'YOU' ? PA(L.by) : undefined })),
    repealed: S.laws.filter((L) => L.stage === 'repealed' && inLeg(L.repM)).map((L) => L.t),
    voided: S.laws.filter((L) => L.stage === 'void' && (L.leg ?? 0) === S.leg).map((L) => L.t),
    budgets: (S.budgets || []).filter((b: any) => b.y >= S.legYear).map((b: any) => ({ y: b.y, saldo: b.saldo, ok: !!b.ok })),
    debt0: ls.debt, debt1: S.debt, saldo: S.saldo,
    share0: ls.share, poll1: poll(),
    promises: isVersus() ? [] : evalPromises().res.map((p: any) => ({ t: p.t, state: p.state })),
    scandals: S.scandalsLeg || 0, crises: Math.max(0, (S.crisisHist || []).length - ls.crises),
  };
  r.pos0 = isVersus() ? Object.fromEntries(ROLES.filter((x) => pl(x).party).map((x) => [x, { ...ownPos(x) }])) : { YOU: { ...S.you } };
  if (isVersus()) {
    r.players = ROLES.map((role) => {
      const P = pl(role), a = ls.stats?.[role] || zero, d = { ...zero };
      for (const k of Object.keys(zero) as (keyof Stats)[]) d[k] = (P.stats[k] || 0) - (a[k] || 0);
      return { role, name: P.party?.n || '–', role2: S.mp!.chan === role ? 'kanzler' : P.role2, d };
    });
  }
  return r;
}

/** Legislatur endet regulär (oder per Neuwahl aus der Regierung heraus): Rückblick zeigen. */
export function openReview(early = false) {
  S.review = build(early); S.phase = 'review'; S.editing = null; S.bview = false;
  log(early ? 'Der Bundestag wird aufgelöst. Zeit für einen Rückblick.' : 'Die Legislatur ist zu Ende. Zeit für einen Rückblick.');
}

/** Vom Rückblick in den Wahlkampf. */
export function reviewDone() {
  if (S.phase !== 'review') return;
  if (isVersus()) { for (const x of ROLES) if (pl(x).party) pl(x).wende = wendeUsed(x); } else S.wende = wendeUsed();
  S.review = null;
  if (isVersus()) mpCampStart(); else startCampaign(false);
}

/** Programm der eigenen Partei: Einzelspieler S.you, Mehrspieler die Partei des Spielers (auf dem Kanzler-Platz S.you). */
export function ownPos(who?: Role): Record<string, number> {
  if (!isVersus()) return S.you;
  const P = pl(who ?? actor());
  return isChan(P.role) ? S.you : P.party!.pos;
}
/** Wie viele Stufen hat die Partei ihr Programm seit Beginn des Rückblicks verschoben? */
export function wendeUsed(who?: Role): number {
  const k = isVersus() ? (who ?? actor()) : 'YOU', p0 = S.review?.pos0?.[k]; if (!p0) return 0;
  const pos = ownPos(who);
  return (axes() as string[]).reduce((s, a) => s + Math.abs((pos[a] ?? 0) - (p0[a] ?? 0)), 0);
}
/** Programm vor dem Wahlkampf nachschärfen. Kostet Glaubwürdigkeit, höchstens WENDE_MAX Stufen. */
export function reposition(a: string, v: number) {
  if (S.phase !== 'review' || !(axes() as string[]).includes(a)) return;
  const who = actor();
  if (isVersus() && !pl(who).party) return;
  const pos = ownPos(who), old = pos[a] ?? 0, nv = clamp(Math.round(v), -2, 2);
  pos[a] = nv;
  if (wendeUsed(who) > WENDE_MAX) { pos[a] = old; return; }
  if (isVersus()) { const P = pl(who); if (isChan(P.role)) P.party!.pos[a] = nv; }
}
